/**
 * UoM Checker Page
 * Allows users to check valid UoM options for any HS Code
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  MagnifyingGlassIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ClipboardDocumentIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

const UoMChecker = () => {
  const [hsCode, setHsCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [testUoM, setTestUoM] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  
  // HS Code search functionality
  const [hsCodeSearch, setHsCodeSearch] = useState('');
  const [hsCodeResults, setHsCodeResults] = useState([]);
  const [showHsCodeDropdown, setShowHsCodeDropdown] = useState(false);
  const [loadingHsCodes, setLoadingHsCodes] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState([]);
  const hsCodeDropdownRef = useRef(null);
  const hsCodeInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Check UoM options for HS Code
  const checkUoMOptions = async () => {
    if (!hsCode.trim()) {
      toast.error('Please enter an HS Code');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setValidationResult(null);

    try {
      const response = await fetch(`/api/reference/uom-options/${hsCode.trim()}`);
      const data = await response.json();

      if (data.success) {
        setResult(data);
        toast.success('UoM options loaded successfully');
      } else {
        setError(data.error || 'Failed to fetch UoM options');
        toast.error('Failed to fetch UoM options');
      }
    } catch (err) {
      console.error('Error fetching UoM options:', err);
      setError('Network error occurred');
      toast.error('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Validate specific UoM for the HS Code
  const validateUoM = async () => {
    if (!hsCode.trim() || !testUoM.trim()) {
      toast.error('Please enter both HS Code and UoM to validate');
      return;
    }

    try {
      const response = await fetch('/api/reference/validate-uom', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          hsCode: hsCode.trim(),
          uom: testUoM.trim()
        })
      });

      const data = await response.json();

      if (data.success) {
        setValidationResult(data.validation);
        if (data.validation.isValid) {
          toast.success('UoM is valid for this HS Code');
        } else {
          toast.error('UoM is not valid for this HS Code');
        }
      } else {
        toast.error('Validation failed');
      }
    } catch (err) {
      console.error('Error validating UoM:', err);
      toast.error('Validation error occurred');
    }
  };

  // Auto-fix UoM
  const autoFixUoM = async () => {
    if (!hsCode.trim() || !testUoM.trim()) {
      toast.error('Please enter both HS Code and UoM to auto-fix');
      return;
    }

    try {
      const response = await fetch('/api/reference/fix-uom', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          hsCode: hsCode.trim(),
          currentUoM: testUoM.trim()
        })
      });

      const data = await response.json();

      if (data.success) {
        if (data.fix.changed) {
          setTestUoM(data.fix.fixedUoM);
          toast.success(`UoM auto-fixed to: ${data.fix.fixedUoM}`);
        } else {
          toast.success('UoM is already correct');
        }
      } else {
        toast.error('Auto-fix failed');
      }
    } catch (err) {
      console.error('Error auto-fixing UoM:', err);
      toast.error('Auto-fix error occurred');
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // HS Code search functionality
  const searchHsCodes = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setHsCodeResults([]);
      setShowHsCodeDropdown(false);
      return;
    }

    setLoadingHsCodes(true);
    try {
      const response = await fetch(`/api/reference/hs-codes?search=${encodeURIComponent(searchTerm)}&limit=10`);
      const data = await response.json();

      if (data.success && data.data) {
        setHsCodeResults(data.data);
        setShowHsCodeDropdown(true);
      } else {
        setHsCodeResults([]);
        setShowHsCodeDropdown(false);
      }
    } catch (err) {
      console.error('Error searching HS codes:', err);
      setHsCodeResults([]);
      setShowHsCodeDropdown(false);
    } finally {
      setLoadingHsCodes(false);
    }
  };

  // Handle HS Code search input change
  const handleHsCodeSearchChange = (value) => {
    setHsCodeSearch(value);
    setHsCode(value); // Also update the main HS code field
    setSelectedIndex(-1); // Reset selection
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      searchHsCodes(value);
    }, 300);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showHsCodeDropdown || hsCodeResults.length === 0) {
      if (e.key === 'Enter') {
        checkUoMOptions();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < hsCodeResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : hsCodeResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < hsCodeResults.length) {
          handleHsCodeSelect(hsCodeResults[selectedIndex]);
        } else {
          checkUoMOptions();
        }
        break;
      case 'Escape':
        setShowHsCodeDropdown(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Handle HS Code selection from dropdown
  const handleHsCodeSelect = (selectedHsCode) => {
    setHsCode(selectedHsCode.code);
    setHsCodeSearch(selectedHsCode.code);
    setShowHsCodeDropdown(false);
    setSelectedIndex(-1);
    setResult(null);
    setValidationResult(null);
    
    // Add to recent searches
    const newSearch = {
      code: selectedHsCode.code,
      description: selectedHsCode.description,
      timestamp: Date.now()
    };
    
    setRecentSearches(prev => {
      const filtered = prev.filter(item => item.code !== selectedHsCode.code);
      const updated = [newSearch, ...filtered].slice(0, 5); // Keep only 5 recent searches
      localStorage.setItem('uomChecker_recentSearches', JSON.stringify(updated));
      return updated;
    });
    
    toast.success(`Selected HS Code: ${selectedHsCode.code}`);
  };

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('uomChecker_recentSearches');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setRecentSearches(parsed);
      } catch (err) {
        console.error('Error loading recent searches:', err);
      }
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (hsCodeDropdownRef.current && !hsCodeDropdownRef.current.contains(event.target)) {
        setShowHsCodeDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            UoM Checker
          </h1>
          <p className="text-gray-600">
            Check valid Unit of Measure (UoM) options for any HS Code
          </p>
        </div>

        {/* Main Input Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="space-y-4">
            {/* HS Code Search Input */}
            <div className="relative" ref={hsCodeDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                HS Code Search
              </label>
              <div className="flex space-x-3">
                <div className="flex-1 relative">
                  <input
                    ref={hsCodeInputRef}
                    type="text"
                    value={hsCodeSearch}
                    onChange={(e) => handleHsCodeSearchChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Search HS Code or description (e.g., 'textile', '6001', 'cotton fabric')"
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoComplete="off"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    {loadingHsCodes ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                    ) : (
                      <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                  
                  {/* HS Code Search Dropdown */}
                  {showHsCodeDropdown && hsCodeResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {hsCodeResults.map((hsCodeItem, index) => (
                        <div
                          key={index}
                          onClick={() => handleHsCodeSelect(hsCodeItem)}
                          className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors ${
                            index === selectedIndex 
                              ? 'bg-blue-50 border-blue-200' 
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start space-x-3">
                            <DocumentTextIcon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                              index === selectedIndex ? 'text-blue-500' : 'text-gray-400'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <div className={`font-medium text-sm ${
                                index === selectedIndex ? 'text-blue-900' : 'text-gray-900'
                              }`}>
                                {hsCodeItem.code}
                              </div>
                              <div className={`text-xs mt-1 line-clamp-2 ${
                                index === selectedIndex ? 'text-blue-700' : 'text-gray-600'
                              }`}>
                                {hsCodeItem.description}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* No Results Message */}
                  {showHsCodeDropdown && hsCodeSearch.length >= 2 && hsCodeResults.length === 0 && !loadingHsCodes && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-4">
                      <div className="text-center text-gray-500">
                        <MagnifyingGlassIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">No HS codes found for "{hsCodeSearch}"</p>
                        <p className="text-xs text-gray-400 mt-1">Try searching with different keywords</p>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={checkUoMOptions}
                  disabled={loading || !hsCode.trim()}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <MagnifyingGlassIcon className="h-4 w-4" />
                  )}
                  <span>{loading ? 'Checking...' : 'Check UoM'}</span>
                </button>
              </div>
              
              {/* Selected HS Code Display */}
              {hsCode && hsCode !== hsCodeSearch && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="text-sm text-blue-800">
                    <span className="font-medium">Selected HS Code:</span> {hsCode}
                  </div>
                </div>
              )}
              
              {/* Recent Searches */}
              {recentSearches.length > 0 && !showHsCodeDropdown && (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-500 mb-2">
                    Recent Searches
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {recentSearches.map((recent, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setHsCode(recent.code);
                          setHsCodeSearch(recent.code);
                        }}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                        title={recent.description}
                      >
                        <DocumentTextIcon className="h-3 w-3 mr-1" />
                        {recent.code}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Example HS Codes and Search Tips */}
            <div className="space-y-2">
              <div className="text-sm text-gray-500">
                <span className="font-medium">Quick Examples:</span>
                <button 
                  onClick={() => {
                    setHsCode('6001.9990');
                    setHsCodeSearch('6001.9990');
                  }} 
                  className="ml-2 text-blue-600 hover:underline"
                >
                  6001.9990 (Textiles)
                </button>
                <button 
                  onClick={() => {
                    setHsCode('8703.2190');
                    setHsCodeSearch('8703.2190');
                  }} 
                  className="ml-2 text-blue-600 hover:underline"
                >
                  8703.2190 (Vehicles)
                </button>
                <button 
                  onClick={() => {
                    setHsCode('2710.1910');
                    setHsCodeSearch('2710.1910');
                  }} 
                  className="ml-2 text-blue-600 hover:underline"
                >
                  2710.1910 (Petroleum)
                </button>
              </div>
              <div className="text-xs text-gray-400">
                <span className="font-medium">Search Tips:</span>
                Try searching by product name (e.g., "cotton", "fabric", "car", "oil") or HS code numbers
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex items-center space-x-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
              <span className="text-red-800 font-medium">Error</span>
            </div>
            <p className="text-red-700 mt-1">{error}</p>
          </div>
        )}

        {/* Results Display */}
        {result && (
          <div className="space-y-6">
            {/* HS Code Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                HS Code Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">HS Code</label>
                  <div className="flex items-center space-x-2">
                    <span className="text-lg font-mono bg-gray-100 px-3 py-1 rounded">
                      {result.hsCode}
                    </span>
                    <button
                      onClick={() => copyToClipboard(result.hsCode)}
                      className="p-1 text-gray-500 hover:text-gray-700"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <span className="text-lg text-gray-900">{result.category}</span>
                </div>
              </div>
            </div>

            {/* Valid UoM Options */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Valid UoM Options ({result.totalOptions})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {result.validUoMs.map((uom, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-md border cursor-pointer transition-colors ${
                      uom.recommended
                        ? 'bg-green-50 border-green-200 text-green-800'
                        : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                    }`}
                    onClick={() => setTestUoM(uom.value)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{uom.label}</span>
                      {uom.recommended && (
                        <span className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded">
                          Recommended
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <InformationCircleIcon className="h-4 w-4 inline mr-1" />
                Click on any UoM to test it below
              </div>
            </div>

            {/* UoM Validation Section */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Test UoM Validation
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Test UoM
                  </label>
                  <div className="flex space-x-3">
                    <input
                      type="text"
                      value={testUoM}
                      onChange={(e) => setTestUoM(e.target.value)}
                      placeholder="Enter UoM to validate (e.g., Kilograms)"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={validateUoM}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                      Validate
                    </button>
                    <button
                      onClick={autoFixUoM}
                      className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
                    >
                      Auto-Fix
                    </button>
                  </div>
                </div>

                {/* Validation Result */}
                {validationResult && (
                  <div className={`p-4 rounded-md border ${
                    validationResult.isValid
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-start space-x-2">
                      {validationResult.isValid ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5" />
                      ) : (
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <div className={`font-medium ${
                          validationResult.isValid ? 'text-green-800' : 'text-red-800'
                        }`}>
                          {validationResult.isValid ? 'Valid UoM' : 'Invalid UoM'}
                        </div>
                        <div className={`text-sm mt-1 ${
                          validationResult.isValid ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {validationResult.message || validationResult.error}
                        </div>
                        {validationResult.suggestedUoM && !validationResult.isValid && (
                          <div className="mt-2">
                            <span className="text-sm text-gray-600">Suggested UoM: </span>
                            <button
                              onClick={() => setTestUoM(validationResult.suggestedUoM)}
                              className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded hover:bg-blue-200"
                            >
                              {validationResult.suggestedUoM}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-6 mt-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">
            How to Use
          </h3>
          <ul className="space-y-2 text-blue-800">
            <li>• <strong>Search HS Code:</strong> Type product name or HS code to find the correct code</li>
            <li>• <strong>Select from dropdown:</strong> Click on any search result to select it</li>
            <li>• <strong>Check UoM:</strong> Click "Check UoM" to see valid options for the selected HS Code</li>
            <li>• <strong>Test UoMs:</strong> Click on any UoM option to test it, or enter manually</li>
            <li>• <strong>Validate:</strong> Use "Validate" to check if a UoM is valid for the HS Code</li>
            <li>• <strong>Auto-Fix:</strong> Use "Auto-Fix" to automatically correct invalid UoMs</li>
            <li>• <strong>Recommendations:</strong> Green highlighted UoMs are recommended for the HS Code</li>
          </ul>
          
          <div className="mt-4 p-3 bg-blue-100 rounded-md">
            <h4 className="font-semibold text-blue-900 mb-2">Search Examples:</h4>
            <div className="text-sm text-blue-800 space-y-1">
              <div>• Search by product: "cotton fabric", "motor car", "petroleum oil"</div>
              <div>• Search by HS code: "6001", "8703", "2710"</div>
              <div>• Search by category: "textile", "vehicle", "chemical"</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UoMChecker;
